import React, { useState, useRef, useCallback } from 'react';
import { Mic, MicOff, X, Activity, AlertCircle, MessageSquare } from 'lucide-react';
import type { InvoiceData } from '../../types';
import { logger } from '../../services/logger';
import { isAIAvailable, generateInvoiceFromText, validateInput } from '../../services/openaiService';

// Web Speech API types
interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

interface SpeechRecognitionInterface extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognitionInterface;
    webkitSpeechRecognition: new () => SpeechRecognitionInterface;
  }
}

interface Props {
  onUpdateInvoice: (data: Partial<InvoiceData>) => void;
}

/**
 * Voice Assistant Component
 * Uses browser's Web Speech API for voice recognition
 * and OpenAI for processing commands
 */
const VoiceAssistant: React.FC<Props> = ({ onUpdateInvoice }) => {
  const [isActive, setIsActive] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<string>('');
  const [lastResponse, setLastResponse] = useState<string>('');
  const accumulatedTranscriptRef = useRef<string>('');

  const recognitionRef = useRef<SpeechRecognitionInterface | null>(null);

  // Check if speech recognition is supported
  const isSpeechSupported =
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const speak = useCallback((text: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      window.speechSynthesis.speak(utterance);
    }
  }, []);

  const processVoiceCommand = useCallback(
    async (text: string) => {
      setIsProcessing(true);
      setError(null);

      try {
        logger.info('Processing voice command', { text });

        // Pre-validate input before sending to AI
        const validation = validateInput(text, { allowRoughPrompts: true });
        
        if (!validation.isValid) {
          const response = validation.rejectionReason || 
            "I can only help with invoice-related requests. Please describe the invoice you'd like to create.";
          setLastResponse(response);
          speak(response);
          
          // Show suggestions if available
          if (validation.suggestions && validation.suggestions.length > 0) {
            logger.info('Voice input suggestions', { suggestions: validation.suggestions });
          }
          return;
        }

        // Use OpenAI to extract invoice updates from the voice command
        const updates = await generateInvoiceFromText(
          `Extract any invoice updates from this voice command. The user said: "${text}". 
         Look for: client names, company names, service descriptions, quantities, rates, amounts.
         Return the data that should be updated on the invoice.`,
          3,
          { allowRoughPrompts: true }
        );

        if (Object.keys(updates).length > 0) {
          onUpdateInvoice(updates);
          const response = "I've updated the invoice with the information you provided.";
          setLastResponse(response);
          speak(response);
          logger.info('Voice command processed successfully', { updates });
        } else {
          const response =
            "I couldn't extract invoice information from that. Please try again with details like client name, service, or amount.";
          setLastResponse(response);
          speak(response);
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Processing failed';
        logger.error('Voice command processing failed', { error: errorMsg });
        
        // Provide user-friendly error messages
        let userMessage = "Sorry, I couldn't process that command.";
        if (errorMsg.includes('INVALID_INPUT') || errorMsg.includes('invoice-related')) {
          userMessage = "I can only help with invoice details. Try saying something like 'Add consulting for 5 hours at $100 per hour'.";
        } else if (errorMsg.includes('RATE_LIMIT')) {
          userMessage = "I'm getting too many requests. Please wait a moment and try again.";
        }
        
        setError(userMessage);
        speak(userMessage);
      } finally {
        setIsProcessing(false);
      }
    },
    [onUpdateInvoice, speak]
  );

  const startListening = useCallback(() => {
    if (!isSpeechSupported) {
      setError('Speech recognition not supported in this browser');
      return;
    }

    if (!isAIAvailable()) {
      setError('AI features not available. Check API key.');
      return;
    }

    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();

      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onstart = () => {
        setIsListening(true);
        setError(null);
        setTranscript('');
        accumulatedTranscriptRef.current = '';
        logger.info('Voice recognition started (continuous mode)');
      };

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += result;
          } else {
            interimTranscript += result;
          }
        }

        if (finalTranscript) {
          accumulatedTranscriptRef.current += ' ' + finalTranscript;
        }

        // Show accumulated + interim for live feedback
        const displayText = (accumulatedTranscriptRef.current + ' ' + interimTranscript).trim();
        setTranscript(displayText);
      };

      recognitionRef.current.onerror = (event) => {
        logger.warn('Speech recognition error', { error: event.error });
        if (event.error === 'not-allowed') {
          setError('Microphone access denied. Please allow microphone permissions.');
        } else if (event.error === 'no-speech') {
          setError('No speech detected. Please try again.');
        } else if (event.error === 'aborted') {
          // Silence aborted errors (happens on manual stop)
        } else {
          setError(`Recognition error: ${event.error}`);
        }
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
        logger.info('Voice recognition ended');
      };

      recognitionRef.current.start();
    } catch (err) {
      logger.error('Failed to start speech recognition', { error: err });
      setError('Failed to start voice recognition');
    }
  }, [isSpeechSupported, processVoiceCommand]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);

    // Process accumulated transcript on manual stop
    const accumulated = accumulatedTranscriptRef.current.trim();
    if (accumulated) {
      processVoiceCommand(accumulated);
    }
  }, [processVoiceCommand]);

  const toggleActive = () => {
    if (isActive) {
      stopListening();
      setIsActive(false);
      setError(null);
      setTranscript('');
      setLastResponse('');
    } else {
      setIsActive(true);
    }
  };

  if (!isActive) {
    return (
      <button
        onClick={toggleActive}
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl shadow-lg shadow-cyan-500/30 flex items-center justify-center text-white hover:scale-110 transition-all z-50 no-print"
        title="Open Voice Assistant"
      >
        <Mic size={24} />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-80 bg-slate-900 border border-slate-700 rounded-3xl shadow-2xl z-50 no-print overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-700/50 bg-slate-800/50">
        <div className="flex items-center gap-3">
          <div
            className={`w-3 h-3 rounded-full ${isListening ? 'bg-green-500 animate-pulse' : isProcessing ? 'bg-amber-500 animate-pulse' : 'bg-slate-500'}`}
          />
          <span className="text-sm font-bold text-white">
            {isListening ? 'Listening...' : isProcessing ? 'Processing...' : 'Voice Assistant'}
          </span>
        </div>
        <button
          onClick={toggleActive}
          className="text-slate-400 hover:text-white transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Status/Error */}
        {error && (
          <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 p-3 rounded-xl">
            <AlertCircle size={14} />
            <span>{error}</span>
          </div>
        )}

        {/* Transcript */}
        {transcript && (
          <div className="bg-slate-800/50 p-3 rounded-xl">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
              You said:
            </p>
            <p className="text-sm text-white">{transcript}</p>
          </div>
        )}

        {/* Last Response */}
        {lastResponse && !isListening && !isProcessing && (
          <div className="bg-cyan-500/10 border border-cyan-500/20 p-3 rounded-xl">
            <p className="text-[10px] font-bold text-cyan-500 uppercase tracking-wider mb-1">
              Assistant:
            </p>
            <p className="text-sm text-cyan-100">{lastResponse}</p>
          </div>
        )}

        {/* Instructions */}
        {!transcript && !error && !lastResponse && (
          <div className="text-center py-4">
            <MessageSquare className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <p className="text-xs text-slate-500">
              Click the microphone and speak your invoice details
            </p>
            <p className="text-[10px] text-slate-600 mt-2">
              Example: "Add consulting service for 5 hours at $150 per hour"
            </p>
          </div>
        )}

        {/* Mic Button */}
        <button
          onClick={isListening ? stopListening : startListening}
          disabled={isProcessing}
          className={`w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-3 transition-all ${
            isListening
              ? 'bg-red-500 text-white hover:bg-red-600'
              : isProcessing
                ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:scale-[1.02]'
          }`}
        >
          {isListening ? (
            <>
              <MicOff size={20} />
              <span>Stop Listening</span>
            </>
          ) : isProcessing ? (
            <>
              <Activity size={20} className="animate-spin" />
              <span>Processing...</span>
            </>
          ) : (
            <>
              <Mic size={20} />
              <span>Start Listening</span>
            </>
          )}
        </button>

        {/* Browser Support Warning */}
        {!isSpeechSupported && (
          <p className="text-[10px] text-amber-500 text-center">
            Speech recognition not supported. Try Chrome or Edge.
          </p>
        )}
      </div>
    </div>
  );
};

export default VoiceAssistant;
